/**
 * Pure calculation core — no React, no DOM, no localStorage. Framework-agnostic so
 * the trainer sign-off (SAD §11) targets one reviewable file and the ≤200 ms
 * responsiveness NFR (SAD §10 QG-2) is trivially met. All tunable numbers live in
 * `constants.ts` (T1), never here.
 */
import {
  ACTIVITY_PRESETS,
  INPUT_BOUNDS,
  KCAL_PER_GRAM,
  NUTRITION_GOALS,
  SAFE_MINIMUM_FLOOR,
  type ActivityLevel,
  type InputField,
  type NutritionGoal,
} from '../constants'

/** Raw form state — numbers arrive as strings, selects may be unset. */
export interface RawCalculatorInput {
  age: string
  height: string
  weight: string
  activity: ActivityLevel | ''
  goal: NutritionGoal | ''
}

/** Validated, parsed input the compute functions operate on. */
export interface CalculatorInput {
  age: number
  height: number
  weight: number
  activity: ActivityLevel
  goal: NutritionGoal
}

export type CalculatorField = keyof RawCalculatorInput

/** Why a field failed — the UI maps this to plain Ukrainian copy from `constants`. */
export type FieldErrorKind = 'required' | 'range'

export type ValidationErrors = Partial<Record<CalculatorField, FieldErrorKind>>

export type ValidationResult =
  | { ok: true; input: CalculatorInput }
  | { ok: false; errors: ValidationErrors }

export interface Macros {
  protein: number
  fat: number
  carb: number
}

export interface CalculationResult {
  /** Displayed daily energy in kcal — clamped to the safe floor when applicable. */
  energy: number
  /** True when `energy` was raised to the safe minimum floor (AC-04). */
  clamped: boolean
  /** Macro breakdown in grams, derived from the displayed energy. */
  macros: Macros
  goal: NutritionGoal
}

export type CalculationOutcome =
  | { ok: true; result: CalculationResult }
  | { ok: false; errors: ValidationErrors }

const NUMERIC_FIELDS: readonly InputField[] = ['age', 'height', 'weight']

function isActivityLevel(value: string): value is ActivityLevel {
  return ACTIVITY_PRESETS.some((preset) => preset.id === value)
}

function isNutritionGoal(value: string): value is NutritionGoal {
  return NUTRITION_GOALS.some((goal) => goal.id === value)
}

/**
 * Per-field validation: required-present (AC-07 support) then within-bounds (AC-02).
 * Never throws — always returns a typed result with per-field error keys.
 */
export function validateInputs(raw: RawCalculatorInput): ValidationResult {
  const errors: ValidationErrors = {}

  for (const field of NUMERIC_FIELDS) {
    const rawValue = raw[field].trim()
    if (rawValue === '') {
      errors[field] = 'required'
      continue
    }
    const value = Number(rawValue)
    const bounds = INPUT_BOUNDS[field]
    if (!Number.isFinite(value) || value < bounds.min || value > bounds.max) {
      errors[field] = 'range'
    }
  }

  if (raw.activity === '' || !isActivityLevel(raw.activity)) errors.activity = 'required'
  if (raw.goal === '' || !isNutritionGoal(raw.goal)) errors.goal = 'required'

  if (Object.keys(errors).length > 0) return { ok: false, errors }

  return {
    ok: true,
    input: {
      age: Number(raw.age),
      height: Number(raw.height),
      weight: Number(raw.weight),
      activity: raw.activity as ActivityLevel,
      goal: raw.goal as NutritionGoal,
    },
  }
}

/** Female Mifflin-St Jeor basal metabolic rate — no sex parameter (CONTEXT invariant). */
export function computeBmr(input: Pick<CalculatorInput, 'age' | 'height' | 'weight'>): number {
  return 10 * input.weight + 6.25 * input.height - 5 * input.age - 161
}

/** Total daily energy expenditure — BMR scaled by the activity-preset multiplier. */
export function computeTdee(bmr: number, activity: ActivityLevel): number {
  const preset = ACTIVITY_PRESETS.find((p) => p.id === activity)
  return bmr * (preset?.multiplier ?? 1)
}

/** Applies the goal's energy factor to the maintenance TDEE. */
export function applyGoal(tdee: number, goal: NutritionGoal): number {
  const definition = NUTRITION_GOALS.find((g) => g.id === goal)
  return tdee * (definition?.energyFactor ?? 1)
}

/** Clamps below-floor energy to the safe minimum, flagging when it did (AC-04). */
export function clampToFloor(energy: number): { energy: number; clamped: boolean } {
  if (energy < SAFE_MINIMUM_FLOOR) return { energy: SAFE_MINIMUM_FLOOR, clamped: true }
  return { energy, clamped: false }
}

/** Protein/fat/carb grams derived from the (already clamped) displayed energy. */
export function computeMacros(energy: number, goal: NutritionGoal): Macros {
  const definition = NUTRITION_GOALS.find((g) => g.id === goal)
  const split = definition?.macros ?? { protein: 0.3, fat: 0.3, carb: 0.4 }
  return {
    protein: Math.round((energy * split.protein) / KCAL_PER_GRAM.protein),
    fat: Math.round((energy * split.fat) / KCAL_PER_GRAM.fat),
    carb: Math.round((energy * split.carb) / KCAL_PER_GRAM.carb),
  }
}

function roundEnergy(energy: number): number {
  return Math.round(energy / 10) * 10
}

/**
 * Orchestrates validate → BMR → TDEE → goal → floor clamp → macros into the typed
 * result `CalculatorResults` (T4) and `CalculatorForm` (T5) consume.
 */
export function calculate(raw: RawCalculatorInput): CalculationOutcome {
  const validation = validateInputs(raw)
  if (!validation.ok) return { ok: false, errors: validation.errors }

  const { input } = validation
  const bmr = computeBmr(input)
  const tdee = computeTdee(bmr, input.activity)
  const goalEnergy = applyGoal(tdee, input.goal)
  const { energy, clamped } = clampToFloor(goalEnergy)
  const displayedEnergy = roundEnergy(energy)

  return {
    ok: true,
    result: {
      energy: displayedEnergy,
      clamped,
      macros: computeMacros(displayedEnergy, input.goal),
      goal: input.goal,
    },
  }
}
